import _ from 'lodash';
import { CapturedImage } from 'ui-component/shared/CameraCaptureV2';

// shipment label tracking number parsing
const trackingRegexs = [
  /1Z[A-Z0-9]{16}/,
  /([0-9]{12}|100\d{31}|\d{15}|\d{18}|96\d{20}|96\d{32})/,
  /([A-Z]{2}\d{9}[A-Z]{2}|(420\d{9}(9[2345])?)?\d{20}|(420\d{5})?(9[12345])?(\d{24}|\d{20})|82\d{8})/,
  /[0-9]{4} [0-9]{4} [0-9]{2}/,
];

export const parseTrackingNumber = (scanQuery: string) => {
  const response: string[] = [];
  // first attempt to handle group separated scans
  const splitScan = _.split(scanQuery, '~');
  splitScan.forEach((__) => {
    trackingRegexs.forEach((re) => {
      const regexMatch = scanQuery.match(re);
      if (regexMatch) {
        response.push(regexMatch[0]);
      }
    });
  });
  return response;
};

export const parseStockLocationCode = (
  scannedString: string
): string | null => {
  const stockLocationRegex = /^L[A-Z0-9]{9}$/;
  return stockLocationRegex.test(scannedString) ? scannedString : null;
};

export const parseKitRequestCode = (scannedString: string): string | null => {
  const kitRequestRegex = /^K[A-Z0-9]{9}$/;
  return kitRequestRegex.test(scannedString) ? scannedString : null;
};

export const parseShipmentCode = (scannedString: string): string | null =>
  scannedString.startsWith('ST') && scannedString.length === 10
    ? scannedString
    : null;
// supplier barcode label parsing

export interface ParsedSupplierBarcode {
  cpid: string | null;
  mpn: string | null;
  quant: string | null;
  coo: string | null;
  dateCode: string | null;
  mfgLot: string | null;
  msl?: string | null;
  sku?: string | null;
  preCapturedImage?: CapturedImage | null;
  preCapturedStockDocId?: string | null;
}

const cpidRegexs = [
  /((?<=~P)([A-Z0-9]{12})([^~]*))/,
  /(?<=mc:)([^,]*)/,
  /(?<=CPN:)([^,]*)/,
];
const mpnRegexs = [
  /((?<=~1P)([^~]*))/,
  /(?<=pm:)([^,]*)/,
  /(?<=MPN:)([^ ]*)/,
  /^((?<=1P)([^~]*))$/,
];
const quantRegexs = [
  /((?<=~Q)([^~]*))/,
  /(?<=qty:)([^,]*)/,
  /(?<=QTY:)([^ ]*)/,
  /^((?<=Q)([^~]*))$/,
];
const cooRegexs = [/((?<=~4L)([^~]*))/, /^((?<=4L)([^~]*))$/];
const dateCodeRegexs = [
  /((?<=~9D)([^~]*))/,
  /^((?<=9D)([^~]*))$/,
  /((?<=~10D)([^~]*))/,
  /^((?<=10D)([^~]*))$/,
];
const mfgLotRegexs = [/((?<=~1T)([^~]*))/, /^((?<=1T)([^~]*))$/];

export const replaceGroupSeparators = (scanQuery: string): string =>
  scanQuery.replace(/<GS>/g, '~').replace(/@d029/g, '~');

export const parseSupplierBarcode = (scanQuery: string) => {
  const response: ParsedSupplierBarcode = {
    cpid: null,
    mpn: null,
    quant: null,
    coo: null,
    dateCode: null,
    mfgLot: null,
  };

  cpidRegexs.every((re) => {
    const regexMatch = scanQuery.match(re);
    if (regexMatch) {
      response.cpid = regexMatch[0];
      return false;
    }
    return true;
  });

  mpnRegexs.every((re) => {
    const regexMatch = scanQuery.match(re);
    if (regexMatch) {
      response.mpn = regexMatch[0];
      return false;
    }
    return true;
  });

  quantRegexs.every((re) => {
    const regexMatch = scanQuery.match(re);
    if (regexMatch) {
      response.quant = regexMatch[0];
      return false;
    }
    return true;
  });

  cooRegexs.every((re) => {
    const regexMatch = scanQuery.match(re);
    if (regexMatch) {
      response.coo = regexMatch[0];
      return false;
    }
    return true;
  });

  dateCodeRegexs.every((re) => {
    const regexMatch = scanQuery.match(re);
    if (regexMatch) {
      response.dateCode = regexMatch[0];
      return false;
    }
    return true;
  });

  mfgLotRegexs.every((re) => {
    const regexMatch = scanQuery.match(re);
    if (regexMatch) {
      response.mfgLot = regexMatch[0];
      return false;
    }
    return true;
  });

  // handle single value barcodes with no ecia identifier prefix
  if (!_.compact(_.values(response)).length) {
    const cpidMatch = scanQuery.match(/^[A-Z0-9]{2}[A-Z0-9]{10}$/);
    if (cpidMatch) {
      response.cpid = cpidMatch[0];
    }
    const quantMatch = scanQuery.match(/^[0-9]+$/);
    if (quantMatch) {
      response.quant = quantMatch[0];
    }
    const cooMatch = scanQuery.match(/^[A-Z]{2}$/);
    if (cooMatch) {
      response.coo = cooMatch[0];
    }
    const dateCodeMatch = scanQuery.match(/^[1-9]{2}[0-9]{2}[A-Z]?$/);
    if (dateCodeMatch) {
      response.dateCode = dateCodeMatch[0];
    }
  }
  if (!_.compact(_.values(response)).length) {
    if (_.startsWith(scanQuery, '1P')) {
      response.mpn = scanQuery.slice(2);
    } else {
      response.mpn = scanQuery;
    }
  }

  return response;
};
